/**
 * FRONT-254: the pure halves of the hands-free call — what EDDY is told the
 * person has on screen, where a `ui_navigate` request goes, and when a quiet
 * call should warn and end, and which chat a live call watches for
 * approvals. No React, no router, no fetch, so each rule is pinned by a
 * plain-node unit test.
 */

/** One page of the app, as the sidebar names it, and where it lives. */
interface PageEntry {
  /** `ui_navigate` target — must match `UI_NAVIGATE_PAGES` in src/voice/hands-free.ts. */
  target: string;
  /** What the person sees in the sidebar. */
  label: string;
  path: string;
}

/**
 * The pages `ui_navigate` can open, in the server catalogue's order. The
 * labels mirror the sidebar (`components/sidebar-nav-catalog.ts`); Settings
 * is the avatar menu's personal page.
 */
export const HANDS_FREE_PAGES: readonly PageEntry[] = [
  { target: 'inbox', label: 'Inbox', path: '/' },
  { target: 'chats', label: 'Chats', path: '/chats' },
  { target: 'memory', label: 'Memory', path: '/memory' },
  { target: 'projects', label: 'Projects', path: '/work' },
  { target: 'knowledge', label: 'Knowledge', path: '/files' },
  { target: 'apps', label: 'Apps', path: '/admin/apps' },
  { target: 'agents', label: 'Agents', path: '/agents' },
  { target: 'schedules', label: 'Schedule', path: '/crons' },
  { target: 'skills', label: 'Skills', path: '/skills' },
  { target: 'workflows', label: 'Workflows', path: '/workflows' },
  { target: 'settings', label: 'Settings', path: '/settings' },
];

export type ScreenItemType = 'chat' | 'document' | 'file' | 'project';

export interface ScreenItem {
  type: ScreenItemType;
  id: string;
  title?: string | null;
}

export interface ScreenContext {
  /** The sidebar label of the page, or a plain description of it. */
  page: string;
  item: ScreenItem | null;
}

/** The page a path belongs to — `/` and `/chat` are both the Inbox; anything
 *  else is matched by its first path segment. */
export function pageLabelFor(pathname: string): string {
  if (pathname === '/' || pathname === '/chat') return 'Inbox';
  const isUnder = (path: string) => pathname === path || pathname.startsWith(`${path}/`);
  const hit = HANDS_FREE_PAGES.find((p) => p.path !== '/' && isUnder(p.path));
  if (hit) return hit.label;
  if (pathname.startsWith('/system-settings')) return 'System settings';
  if (pathname.startsWith('/notifications')) return 'Notifications';
  return 'another page';
}

/** An item the URL itself names: a file, a document or a project. */
export function routeItemFor(pathname: string): ScreenItem | null {
  const doc = /^\/files\/doc\/([^/]+)$/.exec(pathname);
  if (doc) return { type: 'document', id: safeDecode(doc[1]) };
  const file = /^\/files\/([^/]+)$/.exec(pathname);
  if (file && file[1] !== 'new') return { type: 'file', id: safeDecode(file[1]) };
  const project = /^\/work\/([^/]+)$/.exec(pathname);
  if (project) return { type: 'project', id: safeDecode(project[1]) };
  return null;
}

function safeDecode(segment: string): string {
  try {
    return decodeURIComponent(segment);
  } catch {
    return segment;
  }
}

/** What is on screen: the page, plus an open chat/document window (which
 *  sits on top of the page, so it wins) or else the item the URL names. */
export function buildScreenContext(pathname: string, openItem: ScreenItem | null): ScreenContext {
  return { page: pageLabelFor(pathname), item: openItem ?? routeItemFor(pathname) };
}

const MAX_TITLE_CHARS = 80;
/** The server's own cap on a screen note (`MAX_SCREEN_NOTE_CHARS`). */
export const MAX_SCREEN_NOTE_CHARS = 500;

function cleanTitle(title: string): string {
  const flat = title.replace(/["\n\r\t]+/g, ' ').replace(/\s+/g, ' ').trim();
  return flat.length > MAX_TITLE_CHARS ? `${flat.slice(0, MAX_TITLE_CHARS - 1)}…` : flat;
}

/**
 * The note EDDY receives, e.g. `[screen] page=Chats item=chat "Pricing" id=web:1`.
 * Only ids and titles the person can already see. Titles are flattened and
 * capped; the whole note never exceeds the server's limit.
 */
export function formatScreenNote(ctx: ScreenContext): string {
  let note = `[screen] page=${ctx.page}`;
  if (ctx.item) {
    const title = ctx.item.title ? cleanTitle(ctx.item.title) : '';
    note += ` item=${ctx.item.type}${title ? ` "${title}"` : ''} id=${ctx.item.id}`;
  } else {
    note += ' item=none';
  }
  return note.slice(0, MAX_SCREEN_NOTE_CHARS);
}

/** Short words for the strip's "EDDY sees" line. */
export function screenLabel(ctx: ScreenContext): string {
  if (ctx.item?.type === 'chat') return ctx.item.title ? `Chat "${cleanTitle(ctx.item.title)}"` : 'A chat';
  if (ctx.item?.type === 'document' || ctx.item?.type === 'file') {
    return ctx.item.title ? `"${cleanTitle(ctx.item.title)}"` : `${ctx.page}, a ${ctx.item.type}`;
  }
  if (ctx.item?.type === 'project') return ctx.item.title ? `Project "${cleanTitle(ctx.item.title)}"` : 'A project';
  return ctx.page === 'another page' ? 'This page' : `${ctx.page} page`;
}

// ── ui_navigate ──────────────────────────────────────────────────────────

export type NavigatePlan =
  | { kind: 'route'; path: string }
  | { kind: 'chat'; sessionId: string }
  | { kind: 'refuse'; error: string };

const ITEM_TARGETS = new Set(['chat', 'file', 'document', 'project']);
const MAX_ID_CHARS = 300;

/**
 * Turn model-written `ui_navigate` arguments into a place to go. Mirrors the
 * server's `parseUiNavigateArgs` — the browser checks again because on the
 * standard engine these arguments come straight off the data channel.
 */
export function planUiNavigate(args: unknown): NavigatePlan {
  const a = args && typeof args === 'object' ? (args as Record<string, unknown>) : {};
  const target = typeof a.target === 'string' ? a.target.trim() : '';
  const page = HANDS_FREE_PAGES.find((p) => p.target === target);
  if (page) return { kind: 'route', path: page.path };
  if (!ITEM_TARGETS.has(target)) {
    return { kind: 'refuse', error: `There is no page called "${target || 'nothing'}", so the screen stayed put.` };
  }
  const id = typeof a.id === 'string' ? a.id.trim() : '';
  if (!id || id.length > MAX_ID_CHARS) {
    return { kind: 'refuse', error: `Opening a ${target} needs its id from a tool result, so the screen stayed put.` };
  }
  const enc = encodeURIComponent(id);
  if (target === 'chat') return { kind: 'chat', sessionId: id };
  if (target === 'file') return { kind: 'route', path: `/files/${enc}` };
  if (target === 'document') return { kind: 'route', path: `/files/doc/${enc}` };
  return { kind: 'route', path: `/work/${enc}` };
}

// ── Quiet-call auto-end ──────────────────────────────────────────────────

/** Nobody spoke for this long: the strip warns. */
export const QUIET_WARN_AFTER_MS = 5 * 60_000;
/** ...and the call ends this long after the warning, unless kept. */
export const QUIET_END_GRACE_MS = 30_000;

export type QuietPhase = { phase: 'ok' } | { phase: 'warn'; secondsLeft: number } | { phase: 'end' };

/**
 * Where a call stands on the quiet clock. Wall-clock based on purpose: a
 * hidden tab slows timers to about once a second, so counting ticks would
 * stretch the five minutes (measured on GPT-Live). The caller resets
 * `lastActivityAt` on speech and holds it at "now" while work or an approval
 * is pending.
 */
export function quietPhase(lastActivityAt: number, now: number): QuietPhase {
  const quietFor = now - lastActivityAt;
  if (quietFor < QUIET_WARN_AFTER_MS) return { phase: 'ok' };
  const left = QUIET_WARN_AFTER_MS + QUIET_END_GRACE_MS - quietFor;
  if (left <= 0) return { phase: 'end' };
  return { phase: 'warn', secondsLeft: Math.ceil(left / 1000) };
}

// ── Approvals on a minimized call ────────────────────────────────────────

/**
 * FRONT-3118: the chat sessions whose status the call polls, so a pending
 * approval reaches the strip even when its pushed event was published on the
 * other backend replica. The chat-status poll carries each session's pending
 * approval ids and refetches the approvals list when they disagree
 * (FRONT-2343); its usual caller is the open chat window, which a minimized
 * call does not have. Only while live: a connecting or ended call has no
 * tool that could be waiting on the person.
 */
export function approvalWatchSessionIds(sessionId: string | null | undefined, live: boolean): string[] {
  return live && sessionId ? [sessionId] : [];
}
